import { downloadCSV, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Link, RouteProps, useNavigate } from "@opendash/router";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Flex,
  Form,
  Result,
  Row,
  Select,
  Spin,
  Steps,
  Table,
  Typography,
  Upload,
} from "antd";
import { Dayjs } from "dayjs";
import { observer } from "mobx-react-lite";
import React, { useContext, useState } from "react";
import { EndDateSelect } from "../../components/EndDateSelect";
import { RouteTypeSelect } from "../../components/RouteTypeSelect";
import { StartDateSelect } from "../../components/StartDateSelect";
import { WeekDaySelect } from "../../components/WeekDaySelect";
import { AgencyContext } from "../../contexts/AgencyContext";
import { importcsv } from "../../importcsv";
import { ImportPageState } from "../ImportPageState";

export const ImportPage = observer(({ state }: RouteProps<ImportPageState>) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const agencyState = useContext(AgencyContext);
  const [routeForm] = Form.useForm();
  const [tripForm] = Form.useForm();
  const [startdates, setStartdates] = useState<Array<Dayjs>>([]);

  if (routeForm) {
    state.setRouteForm(routeForm);
  }

  if (tripForm) {
    state.setTripForm(tripForm);
  }

  React.useEffect(() => {
    if (agencyState.selectedAgency?.id) {
      state.init(agencyState.selectedAgency);
    }
  }, [agencyState.selectedAgency?.id]);

  const steps = [
    {
      key: 1,
      title: "Datei auswählen",
      content: (
        <>
          <Row gutter={[12, 12]}>
            <Col>
              <Button
                icon={<Icon icon="fa:download" />}
                onClick={() => downloadCSV("import_sample.csv", importcsv)}
                target="_blank"
              >
                {t("gtfs:import.button.sample")}
              </Button>
            </Col>
            <Col>
              <Upload {...state.getUploadProps()} fileList={state.fileList}>
                <Button icon={<Icon icon="fa:upload" />}>
                  {t("gtfs:import.button.upload")}
                </Button>
              </Upload>
            </Col>
          </Row>
          {state.tableColumns.length !== 0 && (
            <Table
              size="middle"
              pagination={{ position: ["topLeft"] }}
              style={{ paddingTop: 24 }}
              columns={state.tableColumns}
              dataSource={[
                Object.fromEntries([
                  ["key", "selects"],
                  ...state.tableColumns.map((col) => [
                    col.dataIndex,
                    <Select
                      options={state.selectOptions}
                      value={state.getSelection(col.dataIndex)}
                      onChange={(value: string) =>
                        state.setSelection(col.dataIndex, value)
                      }
                    />,
                  ]),
                ]),
                ...state.tableDataSource,
              ]}
            />
          )}
        </>
      ),
    },
    {
      key: 2,
      title: "Linien Details",
      content:
        state.tempRoutes.length > 0 ? (
          <Form
            key="routeForm"
            form={routeForm}
            autoComplete="off"
            layout="vertical"
          >
            <Collapse>
              {state.tempRoutes.map((tmpRoute) => {
                const index = tmpRoute.key;

                return (
                  <Collapse.Panel
                    header={tmpRoute.name}
                    key={tmpRoute.key}
                    collapsible={tmpRoute.disabled ? "disabled" : "header"}
                    forceRender
                  >
                    <Form.Item
                      name={[index, "route_type"]}
                      label={t("gtfs:route.create.modal.form.type.label")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:route.create.modal.form.type.rules.required.message"
                          ),
                        },
                      ]}
                    >
                      <RouteTypeSelect />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        children="Für alle Linien übernehmen"
                        onClick={() => state.overwriteForAllRoutes(index)}
                      />
                    </Form.Item>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          </Form>
        ) : null,
    },
    {
      key: 3,
      title: "Fahrten Details",
      content:
        state.tempTrip.length > 0 ? (
          <Form
            key="tripForm"
            form={tripForm}
            autoComplete="off"
            layout="vertical"
          >
            <Collapse>
              {state.tempTrip.map((tmpTrip) => {
                const index = tmpTrip.key;

                return (
                  <Collapse.Panel
                    header={tmpTrip.name}
                    key={tmpTrip.key}
                    collapsible={tmpTrip.disabled ? "disabled" : "header"}
                    forceRender
                  >
                    <Form.Item name={[index, "weekdays"]}>
                      <WeekDaySelect />
                    </Form.Item>

                    <Form.Item
                      label={t(
                        "gtfs:trip.create.modal.form.service.start_date.label"
                      )}
                      name={[index, "start_date"]}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:trip.create.modal.form.service.start_date.rules.required"
                          ),
                        },
                      ]}
                    >
                      <StartDateSelect
                        onChange={(newdate: Dayjs) => {
                          const tmpstartdates = [...startdates];
                          tmpstartdates[index] = newdate;
                          setStartdates(tmpstartdates);
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label={t(
                        "gtfs:trip.create.modal.form.service.end_date.label"
                      )}
                      name={[index, "end_date"]}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:trip.create.modal.form.service.end_date.rules.required"
                          ),
                        },
                      ]}
                    >
                      <EndDateSelect startdate={startdates[index]} />
                    </Form.Item>

                    <Form.Item
                      name={[index, "direction_id"]}
                      label={t("gtfs:route.create.modal.form.direction.label")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:route.create.modal.form.direction.rules.required.message"
                          ),
                        },
                      ]}
                    >
                      <Select
                        options={state.directions.map((direction) => {
                          return {
                            value: direction.get("value"),
                            label:
                              direction.get("value") +
                              ": " +
                              direction.get("description"),
                          };
                        })}
                      />
                    </Form.Item>

                    <Form.Item
                      name={[index, "wheelchair_accessible"]}
                      label={t("gtfs:route.create.modal.form.wheelchair.label")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:route.create.modal.form.wheelchair.rules.required.message"
                          ),
                        },
                      ]}
                    >
                      <Select
                        options={state.wheelchairAccessible.map((wheel) => {
                          return {
                            value: wheel.get("value"),
                            label:
                              wheel.get("value") +
                              ": " +
                              wheel.get("description"),
                          };
                        })}
                      />
                    </Form.Item>

                    <Form.Item
                      name={[index, "bikes_allowed"]}
                      label={t("gtfs:route.create.modal.form.bikes.label")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "gtfs:route.create.modal.form.bikes.rules.required.message"
                          ),
                        },
                      ]}
                    >
                      <Select
                        options={state.bikesAllowed.map((bike) => {
                          return {
                            value: bike.get("value"),
                            label:
                              bike.get("value") +
                              ": " +
                              bike.get("description"),
                          };
                        })}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        children="Für alle Fahrten übernehmen"
                        onClick={() => state.overwriteForAllTrips(index)}
                      />
                      {/* <Button
                        children="Für alle Fahrten der selben Linie übernehmen"
                        onClick={() =>
                          state.overwriteForAllTripsOfSameRoute(index)
                        }
                      /> */}
                    </Form.Item>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          </Form>
        ) : null,
    },
    {
      key: 4,
      title: "Importieren",
      content: (
        <div style={{ textAlign: "center" }}>
          {!state.importStarted ? (
            <Result
              status="info"
              title="Alles vorbereitet"
              extra={
                <Button
                  type="primary"
                  onClick={() => state.doImport(agencyState.selectedAgency)}
                  disabled={state.importStarted}
                >
                  Import starten
                </Button>
              }
            />
          ) : null}
          {state.importStarted ? (
            <Result
              title="Wir importieren alle Daten. Bitte warten."
              subTitle="Nach dem Import werden Sie automatisch umgeleitet."
              extra={<Spin size="large" />}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 5,
      title: "Abschluss",
      content: (
        <div style={{ textAlign: "center" }}>
          <Result
            status={"success"}
            title="Daten erfolgreich importiert"
            extra={
              <Button
                type="primary"
                onClick={() => {
                  navigate("/gtfs");
                }}
              >
                Zur Startseite
              </Button>
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <p>
        Importieren Sie hier Ihre bestehenden Linienkonfigurationen zur
        ausgewählten Agency{" "}
        <b>{agencyState?.selectedAgency?.get("agency_name") || ""}</b>.
        <br />
        Bisher können Sie CSV-Dateien importieren.
      </p>

      <Steps
        current={state.currentStep}
        items={steps.map((item) => ({ key: item.key, title: item.title }))}
      />

      <div style={{ paddingTop: 48 }}>{steps[state.currentStep].content}</div>

      <div style={{ paddingTop: 48 }}>
        {state.currentStep > 0 ? (
          <Button
            style={{ margin: "0 8px" }}
            onClick={() => state.back()}
            disabled={state.backButtonDisabled}
          >
            {t("gtfs:global.button.back_label")}
          </Button>
        ) : null}

        {state.currentStep < steps.length - 2 ? (
          <Button
            type="primary"
            onClick={() => state.next()}
            disabled={state.nextButtonDisabled}
          >
            {t("gtfs:global.button.next_label")}
          </Button>
        ) : null}
      </div>

      <Divider />

      <Flex vertical gap={8}>
        <Typography.Title level={5}>
          {t("gtfs:import.kommuter.title")}
        </Typography.Title>
        <Typography.Text>{t("gtfs:import.kommuter.infotext")}</Typography.Text>
        <Link to={"/gtfs/import/kommuter"}>
          <Button>{t("gtfs:import.kommuter.button.label")}</Button>
        </Link>
      </Flex>
    </div>
  );
});
